import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { categories, getProductsByCategory } from './Product.jsx';
import './ProductPage.css';

const ProductPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState(1);
  const [imageErrors, setImageErrors] = useState({});
  const [imageLoading, setImageLoading] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  // Handle URL parameters for category filtering
  useEffect(() => {
    const categoryFromUrl = searchParams.get('category');
    if (categoryFromUrl) {
      const categoryId = parseInt(categoryFromUrl, 10);
      // Validate that the category exists and is a valid number
      if (!isNaN(categoryId) && categories.some(cat => cat.id === categoryId)) {
        setSelectedCategory(categoryId);
      } else {
        // Invalid category, redirect to default (category 1) and update URL
        setSelectedCategory(1);
        setSearchParams({ category: '1' });
      }
    } else {
      // No category parameter, set default and update URL
      setSelectedCategory(1);
      setSearchParams({ category: '1' });
    }
  }, [searchParams, setSearchParams]);

  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(categoryId);
    setSidebarOpen(false);
    // Update URL parameter when category changes
    setSearchParams({ category: categoryId.toString() });
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleImageLoad = (productId) => {
    setImageLoading(prev => ({ ...prev, [productId]: false }));
  };

  const handleImageError = (productId) => {
    setImageErrors(prev => ({ ...prev, [productId]: true }));
    setImageLoading(prev => ({ ...prev, [productId]: false }));
  };

  const handleImageLoadStart = (productId) => {
    setImageLoading(prev => ({ ...prev, [productId]: true }));
  };

  // Navigate to product detail when card is clicked
  const handleCardClick = (productId) => {
    navigate(`/product/${productId}`);
  };

  // Handle Know More button click (prevent event bubbling)
  const handleKnowMoreClick = (e, productId) => {
    e.stopPropagation(); // Prevent card click event
    navigate(`/product/${productId}`);
  };

  const selectedProducts = getProductsByCategory(selectedCategory);
  const selectedCategoryName = categories.find(cat => cat.id === selectedCategory)?.name || '';

  return (
    <div className="productpage-container">
      {/* Mobile Hamburger Button */}
      <button 
        className="productpage-hamburger-btn"
        onClick={toggleSidebar}
        aria-label="Toggle menu"
      >
        <div className={`productpage-hamburger-line ${sidebarOpen ? 'productpage-hamburger-line-1-open' : ''}`}></div>
        <div className={`productpage-hamburger-line ${sidebarOpen ? 'productpage-hamburger-line-2-open' : ''}`}></div>
        <div className={`productpage-hamburger-line ${sidebarOpen ? 'productpage-hamburger-line-3-open' : ''}`}></div>
      </button>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="productpage-overlay"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Sidebar Navigation */}
      <div className={`productpage-sidebar ${sidebarOpen ? 'productpage-sidebar-open' : ''}`}>
        <div className="productpage-sidebar-header">
          <h2 className="productpage-sidebar-title">Categories</h2>
        </div>
        <nav className="productpage-nav">
          {categories.map((category) => (
            <div
              key={category.id}
              className={`productpage-nav-item ${
                selectedCategory === category.id ? 'productpage-nav-item-active' : ''
              }`}
              onClick={() => handleCategoryClick(category.id)}
            >
              <div className="productpage-nav-arrow">
                <svg 
                  className="productpage-arrow-icon" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor"
                >
                  <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"/>
                </svg>
              </div>
              <span className="productpage-nav-text">{category.name}</span>
            </div>
          ))}
        </nav>
      </div>

      {/* Main Content */}
      <div className="productpage-main">
        <div className="productpage-header">
          <h1 className="productpage-main-title">{selectedCategoryName}</h1>
          <div className="productpage-product-count">
            {selectedProducts.length} Product{selectedProducts.length !== 1 ? 's' : ''}
          </div>
        </div>

        <div className="productpage-products-grid">
          {selectedProducts.map((product) => (
            <div 
              key={product.id} 
              className="productpage-product-card"
              onClick={() => handleCardClick(product.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleCardClick(product.id);
                }
              }}
            >
              <div className="productpage-product-image-container">
                {imageLoading[product.id] && (
                  <div className="productpage-image-loading">
                    <div className="productpage-loading-spinner"></div>
                  </div>
                )}
                
                {imageErrors[product.id] ? (
                  <div className="productpage-image-placeholder">
                    <div className="productpage-placeholder-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                        <circle cx="8.5" cy="8.5" r="1.5"/>
                        <polyline points="21,15 16,10 5,21"/>
                      </svg>
                    </div>
                    <span className="productpage-placeholder-text">Image Not Available</span>
                  </div>
                ) : (
                  <img
                    src={product.image}
                    alt={product.name}
                    className="productpage-product-image"
                    onLoadStart={() => handleImageLoadStart(product.id)}
                    onLoad={() => handleImageLoad(product.id)}
                    onError={() => handleImageError(product.id)}
                    loading="lazy"
                    style={{ 
                      opacity: imageLoading[product.id] ? 0 : 1,
                      transition: 'opacity 0.3s ease'
                    }}
                  />
                )}
              </div>
              
              <div className="productpage-product-info">
                <h3 className="productpage-product-name">{product.name}</h3>
                
                {product.description && (
                  <p className="productpage-product-description">
                    {product.description.length > 120 
                      ? `${product.description.substring(0, 120)}...` 
                      : product.description
                    }
                  </p>
                )}

                <div className="productpage-card-actions">
                  <button
                    className="productpage-know-more-btn"
                    onClick={(e) => handleKnowMoreClick(e, product.id)}
                  >
                    Know More
                    <svg className="productpage-btn-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M5 12h14m-7-7l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"/>
                    </svg>
                  </button>
                </div>
              </div>

              {/* Professional overlay effect */}
              <div className="productpage-card-overlay"></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProductPage;
